import { setTokenExpirationHandler } from "@/services/api";
import { useAuthStore } from "@/stores/auth";
import { useBookmarksStore } from "@/stores/bookmark";
import { useCountryStore } from "@/stores/country";
import { useDatabaseStore } from "@/stores/database";
import { useUserFilesStore } from "@/stores/user-file";
import { logger } from "@/utils/debug";
import { debugEnv } from "@/utils/debug/env";
import * as Sentry from "@sentry/react";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";

export function AppInitializer({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const initializeAuth = useAuthStore((state) => state.initializeAuth);
  const handleTokenExpiration = useAuthStore(
    (state) => state.handleTokenExpiration
  );
  const loadProjects = useDatabaseStore((state) => state.loadProjects);
  const fetchBookmarks = useBookmarksStore((state) => state.fetchBookmarks);
  const fetchBookmarkSets = useBookmarksStore(
    (state) => state.fetchBookmarkSets
  );
  const refreshFiles = useUserFilesStore((state) => state.refreshFiles);
  const loadCountries = useCountryStore((state) => state.loadCountries);
  const user = useAuthStore((state) => state.user);
  const loading = useAuthStore((state) => state.loading);

  useEffect(() => {
    if (import.meta.env.DEV) {
      debugEnv();
    }

    initializeAuth();

    const handleExpiration = () => {
      handleTokenExpiration();
      navigate("/login");
    };

    setTokenExpirationHandler(handleExpiration);

    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "europafacts_access_token" && !e.newValue) {
        handleExpiration();
      }
    };

    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("storage", handleStorageChange);
      setTokenExpirationHandler(() => {});
    };
  }, [initializeAuth, handleTokenExpiration, navigate]);

  const hasLoadedDataRef = useRef<string | null>(null);

  useEffect(() => {
    if (!loading && user) {
      if (hasLoadedDataRef.current !== user.id) {
        hasLoadedDataRef.current = user.id;

        Sentry.setUser({
          id: user.id,
          username: user.username,
          email: user.email,
        });

        loadCountries().catch((error) => {
          logger.warn("Failed to load countries:", error);
        });
        loadProjects();
        fetchBookmarks();
        fetchBookmarkSets();
        refreshFiles();
      }
    } else if (!loading && !user) {
      hasLoadedDataRef.current = null;
      Sentry.setUser(null);
    }
  }, [user?.id, loading]);

  return <>{children}</>;
}
